import { useRef, useState } from "react";
import CnpjUtils from "../../../shared/utils/CnpjUtils";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";

const formatClientByCnpjResponse = clientData => {
	const data = clientData?.resultado ?? clientData;

	return {
		clientDocument: CnpjUtils.formatAlphanumericCnpj(
			data?.cnpj ||
			data?.cnpjCliente ||
			data?.documento ||
			""
		),
		clientName:
			data?.razaoSocial ||
			data?.nome ||
			data?.nomeCliente ||
			""
	};
};

export function useClientByCnpj() {
	const [client, setClient] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const cacheRef = useRef(new Map());

	const searchClientByCnpj = async formattedCnpj => {
		const cnpj = CnpjUtils.cleanAlphanumericCnpj(formattedCnpj);

		if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) {
			const errorMessage = "CNPJ inválido.";

			setClient(null);
			setError(errorMessage);

			return {
				ok: false,
				error: errorMessage
			};
		}

		if (cacheRef.current.has(cnpj)) {
			const cachedClient = cacheRef.current.get(cnpj);

			setClient(cachedClient);
			setError(null);

			return {
				ok: true,
				client: cachedClient
			};
		}

		try {
			setLoading(true);
			setError(null);

			const {
				accessToken,
				clientId
			} = getAuthParams();

			const params = new URLSearchParams();

			params.append("cnpj", cnpj);

			const response = await fetch(
				process.env.REACT_APP_ESSILOR_API +
					`pacto-rebates-simulador/v1/cadastro/cliente?${params.toString()}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"access_token": accessToken,
						"client_id": clientId
					}
				}
			);

			if (!response.ok) {
				await throwResponseError(
					response,
					"Cliente não encontrado."
				);
			}

			const data = await response.json();
			const formattedClient = formatClientByCnpjResponse(data);

			cacheRef.current.set(cnpj, formattedClient);
			setClient(formattedClient);
			setError(null);

			return {
				ok: true,
				client: formattedClient
			};
		} catch (requestError) {
			const errorMessage =
				requestError.message ||
				"Não foi possível consultar o CNPJ.";

			setClient(null);
			setError(errorMessage);

			return {
				ok: false,
				error: errorMessage
			};
		} finally {
			setLoading(false);
		}
	};

	const clearClient = () => {
		setClient(null);
		setError(null);
	};

	const clearCache = () => {
		cacheRef.current.clear();
	};

	return {
		client,
		loading,
		error,
		searchClientByCnpj,
		clearClient,
		clearCache
	};
}